import { MouseEvent, useEffect, useMemo, useState } from 'react';

import { format, parse } from 'date-fns';
import { useForm } from 'react-hook-form';
import toast from 'react-hot-toast';

import {
  IndexRequestSchemaDataItem,
  PutApiV5FirmsRequestsIdBody,
  RequestSchemaStatus,
  SignableDocumentSchema,
  usePutApiV5FirmsRequestsId,
} from '@liscio/api';
import {
  useRequestNavigation,
  SignedUrlPreview,
  useRequestStore,
  useSignedUrl,
  WorkflowSignableDocument,
  WorkflowSignablePreviewDocument,
} from '@liscio/common';
import {
  Stack,
  InputLabel,
  Link,
  TextField,
  Icon,
  Button,
  styled,
  Typography,
  Skeleton,
} from '@liscio/ui';

import { StyledButton } from './StyledComponents';
import { SelectOwnerDropdown } from '../../../../components/SelectOwnerDropdown/SelectOwnerDropdown';
import { ClientPreviewDialog } from '../ClientPreviewDialog/ClientPreviewDialog';
import { PaymentReminderSettings } from '../PaymentReminderSettings/PaymentReminderSettings';
import { RequestRecipientSelector } from '../RequestRecipientSelector';
import { RHFDateInput } from 'components';
import { useGetAllItemsInSections } from 'modules/requests/hooks/useGetAllItemsInSections';
import { useReorderPreviewItems } from 'modules/requests/hooks/useReorderPreviewItems';
import { useRequestFilterStore } from 'modules/requests/hooks/useRequestFilterStore';
import { findSignableItem } from 'modules/requests/utils';
import { getSignedDocumentsFromItems } from 'modules/requests/utils/getSignedDocumentsFromItems';

type EditDrawerProps = {
  drawerTitle?: string;
  open: boolean;
  row: Pick<IndexRequestSchemaDataItem, 'uuid'>;
  onCancel?: (event: MouseEvent) => void;
  onClose: () => void;
  width?: string;
  maxWidth?: string;
};

type FormDataType = {
  title?: string;
  owner_id?: string;
  due_date?: string;
  assignedAccountId?: string;
  assignedContactIds?: string[];
};

const DATE_FORMAT = 'MMM d, yyyy';

const InputLabelStyled = styled(InputLabel)(() => ({
  fontSize: 0.75,
  fontWeight: 600,
}));

/**
 * This component is used to edit a request in a drawer.
 */
export function RequestEdit({
  drawerTitle,
  open,
  row,
  onCancel,
  onClose,
  width,
  maxWidth,
}: EditDrawerProps) {
  const {
    requestId,
    rawRequestData: requestData,
    isLoading,
    refetchRequest: refetch,
  } = useRequestStore();

  const { mutateAsync: updateRequest } = usePutApiV5FirmsRequestsId();
  const getRefetchRequests = useRequestFilterStore(
    (state) => state.getRefetchRequests
  );
  const { isTaxDeliveryRequest } = useRequestNavigation();

  // Prefetch workflow GraphQL for Client Preview (section → template mapping) before the dialog mounts.
  useReorderPreviewItems(
    requestId || '',
    Boolean(requestId) && isTaxDeliveryRequest
  );

  const { items, isLoading: signableDocumentsLoading } =
    useGetAllItemsInSections(isTaxDeliveryRequest);
  const [pdfToken, setPdfToken] = useState<string | null>(null);
  const {
    signedUrlData,
    setRetrievalToken,
    refreshSignedUrl,
    clearSignedUrl,
    isLoading: isSignedUrlLoading,
  } = useSignedUrl();

  const [previewOpen, setPreviewOpen] = useState(false);

  const signableDocuments = useMemo(
    () => getSignedDocumentsFromItems(items),
    [items]
  );
  const signableItem = useMemo(() => findSignableItem(items), [items]);
  const signableObject = signableItem?.dependency?.object as
    | SignableDocumentSchema
    | undefined;

  const { handleSubmit, register, control, reset, formState } =
    useForm<PutApiV5FirmsRequestsIdBody>({
      defaultValues: {
        title: '',
        owner_id: '',
        due_date: '',
        assigned_account_id: '',
        assigned_contact_ids: [],
      },
    });

  useEffect(() => {
    if (requestData && open) {
      let dueDate = '';
      try {
        dueDate = requestData.due_date
          ? new Date(requestData.due_date).toISOString().split('T')[0]
          : '';
      } catch (error) {
        console.error('Error parsing date:', error);
      }

      reset({
        title: requestData.title || '',
        owner_id: requestData.owner?.uuid || '',
        due_date: dueDate,
      });
    }
  }, [requestData, reset, open]);

  const onSubmit = (formData: FormDataType) => {
    let isoDate = '';
    try {
      isoDate = formData.due_date
        ? format(
            parse(formData.due_date, 'yyyy-MM-dd', new Date()),
            "yyyy-MM-dd'T'HH:mm:ss.SSSxxx"
          )
        : '';
    } catch (error) {
      console.error('Error parsing date:', error);
    }

    if (requestData?.status !== RequestSchemaStatus.draft) {
      delete formData.assignedAccountId;
      delete formData.assignedContactIds;
    }

    // Handle form submission
    updateRequest(
      {
        id: row.uuid || '',
        data: {
          title: formData.title,
          assigned_account_id: formData.assignedAccountId,
          ...(formData.owner_id && { owner_id: formData.owner_id }),
          ...(isoDate !== '' && { due_date: isoDate }),
          ...(formData.assignedContactIds && {
            assigned_contact_ids: formData.assignedContactIds,
          }),
        },
      },
      {
        onSuccess: () => {
          const refetchRequests = getRefetchRequests();

          // Refetch the requests list
          if (refetchRequests) {
            refetchRequests();
          }

          refetch(); // Refetch the request data

          toast.success('Request updated successfully.');
          onClose();
        },
        onError: (error) => {
          toast.error('Error updating request.');
          console.error('Error updating request:', error);
          onClose();
        },
      }
    );
  };

  const handleSourceFileClick = async (token: string | null) => {
    if (token) {
      setPdfToken(token);
      setRetrievalToken(token);
      await refreshSignedUrl();
    }
  };

  if (isLoading) {
    return <Skeleton />;
  }

  return (
    <>
      <form
        onSubmit={handleSubmit(onSubmit)}
        style={{ height: '100%', width, maxWidth }}
      >
        <Stack height="100%">
          <Stack flexGrow={0.97} gap={2} padding="1rem">
            {drawerTitle && <Typography variant="h5">{drawerTitle}</Typography>}
            {requestData?.source_documents &&
              requestData.source_documents.length > 0 && (
                <Stack gap={1}>
                  <InputLabelStyled>SOURCE FILE</InputLabelStyled>
                  <Link
                    href={'#'}
                    onClick={() =>
                      handleSourceFileClick(
                        requestData.source_documents[0].retrieval_token
                      )
                    }
                    display="flex"
                    alignItems="start"
                    sx={{ wordBreak: 'break-word' }}
                    disabled={isSignedUrlLoading}
                  >
                    <Icon icon="filePdf" sx={{ marginRight: '5px' }} />
                    {requestData?.source_documents?.[0]?.filename}
                  </Link>
                </Stack>
              )}
            {(signableDocuments.length > 0 || signableDocumentsLoading) &&
              isTaxDeliveryRequest && (
                <Stack gap={1}>
                  <InputLabelStyled>SIGNED DOCUMENT</InputLabelStyled>
                  {signableDocumentsLoading && <Skeleton />}
                  {signableDocuments.map((document) => (
                    <Link
                      key={document.uuid}
                      href={'#'}
                      onClick={() =>
                        handleSourceFileClick(document.retrieval_token)
                      }
                      display="flex"
                      alignItems="start"
                      sx={{ wordBreak: 'break-word' }}
                      disabled={isSignedUrlLoading}
                    >
                      <Icon icon="filePdf" sx={{ marginRight: '5px' }} />
                      {document.filename}
                    </Link>
                  ))}
                </Stack>
              )}
            <Stack gap={1}>
              <InputLabelStyled>REQUEST TITLE</InputLabelStyled>
              <TextField
                variant="outlined"
                fullWidth
                disabled={requestData?.status !== 'draft'}
                {...register('title')}
              />
            </Stack>
            <Stack gap={1}>
              <InputLabelStyled>OWNER</InputLabelStyled>
              <SelectOwnerDropdown control={control} name="owner_id" />
            </Stack>
            <Stack gap={1}>
              <InputLabelStyled>DUE DATE</InputLabelStyled>
              <RHFDateInput
                name="due_date"
                control={control}
                format={DATE_FORMAT}
                valueFormat="yyyy-MM-dd"
                minDate={new Date()}
                placeholder="Select due date"
              />
            </Stack>
            <Stack>
              <RequestRecipientSelector
                name="recipients"
                control={control}
                account={requestData?.assigned_account}
                assignedContacts={requestData?.assigned_contacts || []}
                disabled={requestData?.status !== RequestSchemaStatus.draft}
              />
            </Stack>
            {isTaxDeliveryRequest && signableItem && (
              <Stack gap={1}>
                <InputLabelStyled>CLIENT SIGNATURES</InputLabelStyled>
                <Stack
                  direction="row"
                  alignItems="center"
                  justifyContent="space-between"
                >
                  <Typography variant="body2">
                    {signableItem.prompt}
                  </Typography>
                  {signableObject?.document_to_sign?.retrieval_token && (
                    <WorkflowSignablePreviewDocument
                      retrievalToken={
                        signableObject.document_to_sign.retrieval_token
                      }
                    />
                  )}
                </Stack>
                <WorkflowSignableDocument
                  signers={signableObject?.signatories}
                  disabled
                />
              </Stack>
            )}
            {isTaxDeliveryRequest && requestData?.reminder_type && (
              <PaymentReminderSettings
                requestId={row.uuid}
                currentReminderType={requestData.reminder_type}
                disabled={requestData?.status !== RequestSchemaStatus.draft}
              />
            )}
          </Stack>
          <Stack
            gap={1}
            padding={2}
            sx={(theme) => ({
              borderTop: `1px solid ${theme.palette.divider}`,
            })}
          >
            {isTaxDeliveryRequest && (
              <Button
                fullWidth
                variant="outlined"
                color="primary"
                onClick={(e) => {
                  e.preventDefault();
                  setPreviewOpen(true);
                }}
                startIcon={<Icon icon="show" />}
              >
                Preview what your client will see
              </Button>
            )}
            <Stack
              direction="row"
              justifyContent="space-evenly"
              gap={2}
            >
              {onCancel && (
                <StyledButton
                  variant="outlined"
                  color="primary"
                  onClick={(e) => {
                    e.preventDefault();
                    onCancel(e);
                  }}
                  fullWidth
                >
                  Cancel
                </StyledButton>
              )}
              <StyledButton
                variant="contained"
                color="primary"
                type="submit"
                disabled={!formState.isDirty}
                fullWidth
              >
                Save
              </StyledButton>
            </Stack>
          </Stack>
        </Stack>
      </form>
      {pdfToken && signedUrlData && (
        <SignedUrlPreview
          onClose={() => {
            setPdfToken(null);
            clearSignedUrl();
          }}
          retrievalToken={pdfToken}
          signedUrl={signedUrlData.signed_url}
          fileName={signedUrlData.filename ?? 'File'}
        />
      )}
      {isTaxDeliveryRequest && previewOpen && (
        <ClientPreviewDialog
          open={previewOpen}
          onClose={() => setPreviewOpen(false)}
        />
      )}
    </>
  );
}
